import { isNfseFieldRequired, type NfseMunicipalProfile } from "./profiles";

export type NfseReadinessSeverity = "blocking" | "warning";

export type NfseCorrectionTarget =
  | "clinicSettings"
  | "serviceItem"
  | "buyer"
  | "saleReview"
  | "focus";

export interface NfseReadinessCheck {
  code: string;
  severity: NfseReadinessSeverity;
  target: NfseCorrectionTarget;
  messageKey: string;
  context?: Record<string, string | number | boolean | null>;
}

export interface NfseReadinessInput {
  profile: NfseMunicipalProfile;
  clinic: {
    cnpj?: string | null;
    companyName?: string | null;
    municipalRegistration?: string | null;
    municipalCode?: string | null;
    focusApiTokenConfigured?: boolean | null;
    certificateConfigured?: boolean | null;
  };
  buyer: {
    document?: string | null;
    name?: string | null;
    addressComplete?: boolean | null;
  };
  serviceItems: Array<{
    id: string;
    itemId?: string | null;
    itemName: string;
    quantity: number;
    unitPrice: number;
    lineTotal: number;
    fiscal?: {
      nfse_service_list_item?: string | null;
      nfse_iss_rate?: number | null;
    } | null;
  }>;
}

interface AddCheckInput {
  code: string;
  severity: NfseReadinessSeverity;
  target: NfseCorrectionTarget;
  context?: NfseReadinessCheck["context"];
}

function digitsOnly(value?: string | null): string {
  return value?.replace(/\D/g, "") ?? "";
}

function hasText(value?: string | null): boolean {
  return Boolean(value?.trim());
}

function messageKeyFor(code: string): string {
  const keyByCode: Record<string, string> = {
    CLINIC_CNPJ_INVALID: "nfse.readiness.clinicCnpjInvalid",
    CLINIC_COMPANY_NAME_MISSING: "nfse.readiness.clinicCompanyNameMissing",
    CLINIC_MUNICIPAL_REGISTRATION_MISSING: "nfse.readiness.clinicMunicipalRegistrationMissing",
    CLINIC_MUNICIPAL_CODE_INVALID: "nfse.readiness.clinicMunicipalCodeInvalid",
    FOCUS_TOKEN_MISSING: "nfse.readiness.focusTokenMissing",
    CERTIFICATE_NOT_CONFIRMED: "nfse.readiness.certificateNotConfirmed",
    BUYER_DOCUMENT_MISSING: "nfse.readiness.buyerDocumentMissing",
    BUYER_NAME_MISSING: "nfse.readiness.buyerNameMissing",
    NO_SERVICE_ITEMS: "nfse.readiness.noServiceItems",
    SERVICE_LIST_ITEM_MISSING: "nfse.readiness.serviceListItemMissing",
    ISS_RATE_MISSING: "nfse.readiness.issRateMissing",
    SERVICE_VALUE_INVALID: "nfse.readiness.serviceValueInvalid",
  };

  return keyByCode[code] ?? "nfse.readiness.unexpected";
}

export function buildNfseReadiness(input: NfseReadinessInput): {
  all: NfseReadinessCheck[];
  blocking: NfseReadinessCheck[];
  warnings: NfseReadinessCheck[];
  canIssue: boolean;
} {
  const all: NfseReadinessCheck[] = [];
  const addCheck = ({ code, severity, target, context }: AddCheckInput) => {
    all.push({
      code,
      severity,
      target,
      messageKey: messageKeyFor(code),
      ...(context ? { context } : {}),
    });
  };

  if (digitsOnly(input.clinic.cnpj).length !== 14) {
    addCheck({
      code: "CLINIC_CNPJ_INVALID",
      severity: "blocking",
      target: "clinicSettings",
    });
  }

  if (!hasText(input.clinic.companyName)) {
    addCheck({
      code: "CLINIC_COMPANY_NAME_MISSING",
      severity: "blocking",
      target: "clinicSettings",
    });
  }

  if (!hasText(input.clinic.municipalRegistration)) {
    addCheck({
      code: "CLINIC_MUNICIPAL_REGISTRATION_MISSING",
      severity: "blocking",
      target: "clinicSettings",
    });
  }

  if (digitsOnly(input.clinic.municipalCode).length !== 7) {
    addCheck({
      code: "CLINIC_MUNICIPAL_CODE_INVALID",
      severity: "blocking",
      target: "clinicSettings",
    });
  }

  if (!input.clinic.focusApiTokenConfigured) {
    addCheck({
      code: "FOCUS_TOKEN_MISSING",
      severity: "blocking",
      target: "clinicSettings",
    });
  }

  if (!input.clinic.certificateConfigured) {
    addCheck({
      code: "CERTIFICATE_NOT_CONFIRMED",
      severity: "warning",
      target: "clinicSettings",
    });
  }

  const buyerDocumentLength = digitsOnly(input.buyer.document).length;
  if (buyerDocumentLength !== 11 && buyerDocumentLength !== 14) {
    addCheck({
      code: "BUYER_DOCUMENT_MISSING",
      severity: "blocking",
      target: "buyer",
    });
  }

  if (!hasText(input.buyer.name)) {
    addCheck({
      code: "BUYER_NAME_MISSING",
      severity: "blocking",
      target: "buyer",
    });
  }

  if (input.serviceItems.length === 0) {
    addCheck({
      code: "NO_SERVICE_ITEMS",
      severity: "blocking",
      target: "saleReview",
    });
  }

  input.serviceItems.forEach((serviceItem) => {
    const context = {
      lineId: serviceItem.id,
      itemId: serviceItem.itemId,
      itemName: serviceItem.itemName,
    };

    if (
      isNfseFieldRequired(input.profile, "nfse_service_list_item") &&
      !hasText(serviceItem.fiscal?.nfse_service_list_item)
    ) {
      addCheck({
        code: "SERVICE_LIST_ITEM_MISSING",
        severity: "blocking",
        target: "serviceItem",
        context,
      });
    }

    if (
      isNfseFieldRequired(input.profile, "nfse_iss_rate") &&
      (!serviceItem.fiscal?.nfse_iss_rate || serviceItem.fiscal.nfse_iss_rate <= 0)
    ) {
      addCheck({
        code: "ISS_RATE_MISSING",
        severity: "blocking",
        target: "serviceItem",
        context,
      });
    }

    if (serviceItem.lineTotal <= 0) {
      addCheck({
        code: "SERVICE_VALUE_INVALID",
        severity: "blocking",
        target: "saleReview",
        context,
      });
    }
  });

  const blocking = all.filter((check) => check.severity === "blocking");
  const warnings = all.filter((check) => check.severity === "warning");

  return {
    all,
    blocking,
    warnings,
    canIssue: blocking.length === 0,
  };
}
